/**
 * ImageGalleryBlock — Web renderer for IMAGE_GALLERY content blocks.
 *
 * Displays images in a responsive grid or horizontal carousel layout
 * with optional captions and lightbox-style alt text.
 */

import { SurfaceImageGalleryConfigResponse } from "@/hooks/hub/useGetHub";
import "./ImageGalleryBlock.scss";

/** Only allow https:// URLs for image sources — prevents data: URI and tracking pixel injection */
const isSafeImageUrl = (url?: string) => !!url && (/^https?:\/\//).test(url);

interface ImageGalleryBlockProps {
    config?: SurfaceImageGalleryConfigResponse;
    sectionTitle?: string;
}

export const ImageGalleryBlock = ({ config, sectionTitle }: ImageGalleryBlockProps) => {
    const images = config?.images || [];
    if (images.length === 0) return null;

    const layout = config?.layout || "grid";
    const columns = Math.max(1, Math.min(config?.columns || 3, 6));

    return (
        <div className="image-gallery-block">
            {sectionTitle && <h2 className="section-title">{sectionTitle}</h2>}
            <div
                className={`image-gallery-block__container image-gallery-block__container--${layout}`}
                style={layout === "grid" ? { gridTemplateColumns: `repeat(${columns}, 1fr)` } : undefined}
            >
                {images.map((img, i) =>
                    <figure key={img._id || i} className="image-gallery-block__item">
                        {isSafeImageUrl(img.imageUrl)
                            ? <img
                                src={img.imageUrl}
                                alt={img.alt || img.caption || `Gallery image ${i + 1}`}
                                className="image-gallery-block__image"
                                loading="lazy"
                            />
                            : <div className="image-gallery-block__placeholder" />
                        }
                        {img.caption &&
                            <figcaption className="image-gallery-block__caption">
                                {img.caption}
                            </figcaption>
                        }
                    </figure>)}
            </div>
        </div>
    );
};
